import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isoDate, localInputValue, userTimezone } from "@/lib/time";

const CATEGORIES = [["PERSONAL", "Personnel"], ["MEETING", "Réunion"], ["WORK", "Travail"], ["GOLF", "Golf"], ["BUSY", "Occupé"], ["FREE", "Libre"]];
const RECUR = [["NONE", "Jamais"], ["DAILY", "Chaque jour"], ["WEEKLY", "Chaque semaine"], ["WEEKDAYS", "Jours ouvrés"], ["MONTHLY", "Chaque mois"], ["CUSTOM", "Personnalisé"]];
const WDAYS = [["MO", "L"], ["TU", "M"], ["WE", "M"], ["TH", "J"], ["FR", "V"], ["SA", "S"], ["SU", "D"]];

const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm";
const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</span>{children}</label>
);

export function emptyEvent(start, end) {
  return { title: "", all_day: false, start: localInputValue(start), end: localInputValue(end), start_date: isoDate(start), end_date: isoDate(start),
    location: "", description: "", category: "PERSONAL", transparency: "busy", recurrence: { freq: "NONE", interval: 1, byweekday: [], until: "" } };
}

export function eventToForm(ev) {
  const rule = ev.recurrence_rule || "";
  const freq = !rule ? "NONE" : rule.includes("BYDAY=MO,TU,WE,TH,FR") ? "WEEKDAYS" : (rule.match(/FREQ=(\w+)/)?.[1] || "NONE");
  const byweekday = (rule.match(/BYDAY=([A-Z,]+)/)?.[1] || "").split(",").filter(Boolean);
  return { title: ev.title, all_day: !!ev.all_day, start: ev.start_utc ? localInputValue(new Date(ev.start_utc)) : "", end: ev.end_utc ? localInputValue(new Date(ev.end_utc)) : "",
    start_date: ev.start_date || "", end_date: ev.end_date ? isoDate(new Date(new Date(ev.end_date + "T00:00").getTime() - 86400000)) : "",
    location: ev.location || "", description: ev.description || "", category: ev.category || "PERSONAL", transparency: ev.transparency || "busy",
    recurrence: { freq: freq === "WEEKLY" && byweekday.length && !rule.includes("MO,TU,WE,TH,FR") ? "WEEKLY" : freq, interval: Number(rule.match(/INTERVAL=(\d+)/)?.[1] || 1), byweekday, until: "" } };
}

export function formToPayload(f) {
  const p = { title: f.title, all_day: f.all_day, location: f.location, description: f.description, category: f.category,
    transparency: f.category === "FREE" ? "free" : f.transparency, timezone: userTimezone(),
    recurrence: { ...f.recurrence, until: f.recurrence.until || null, byweekday: f.recurrence.byweekday?.length ? f.recurrence.byweekday : null } };
  if (f.all_day) {
    p.start_date = f.start_date;
    const ed = new Date((f.end_date || f.start_date) + "T00:00"); ed.setDate(ed.getDate() + 1); p.end_date = isoDate(ed);
  } else { p.start = new Date(f.start).toISOString(); p.end = new Date(f.end).toISOString(); }
  return p;
}

export default function EventDialog({ open, onClose, initial, onSubmit, title = "Nouvel événement", submitLabel = "Créer l'événement" }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial); }, [open, initial]);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setR = (k, v) => setF((x) => ({ ...x, recurrence: { ...x.recurrence, [k]: v } }));
  const submit = async (e) => {
    e?.preventDefault();
    if (!f.title.trim()) return;
    setSaving(true);
    try { await onSubmit(formToPayload(f)); onClose(); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="event-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <input autoFocus data-testid="event-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Titre (ex : Dentiste)" className={`${inputCls} text-base font-medium`} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="event-allday" checked={f.all_day} onChange={(e) => set("all_day", e.target.checked)} /> Journée entière</label>
          {f.all_day ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Du"><input type="date" data-testid="event-start-date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} /></Field>
              <Field label="Au (inclus)"><input type="date" data-testid="event-end-date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} /></Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Début"><input type="datetime-local" data-testid="event-start" value={f.start} onChange={(e) => set("start", e.target.value)} className={inputCls} /></Field>
              <Field label="Fin"><input type="datetime-local" data-testid="event-end" value={f.end} onChange={(e) => set("end", e.target.value)} className={inputCls} /></Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie">
              <select data-testid="event-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Disponibilité">
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[["busy", "Occupé"], ["free", "Libre"]].map(([v, l]) => (
                  <button type="button" key={v} data-testid={`event-transparency-${v}`} onClick={() => set("transparency", v)}
                    className={`flex-1 py-2 text-sm ${f.transparency === v ? (v === "busy" ? "bg-rose-500/20 text-rose-200" : "bg-emerald-500/20 text-emerald-200") : "text-muted-foreground hover:bg-accent"}`}>{l}</button>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Lieu"><input data-testid="event-location" value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Optionnel" className={inputCls} /></Field>
          <Field label="Notes"><textarea data-testid="event-notes" value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Optionnel" className={`${inputCls} resize-none`} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Récurrence">
              <select data-testid="event-recurrence" value={f.recurrence.freq} onChange={(e) => setR("freq", e.target.value)} className={inputCls}>
                {RECUR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            {f.recurrence.freq !== "NONE" && <Field label="Jusqu'au"><input type="date" data-testid="event-until" value={f.recurrence.until} onChange={(e) => setR("until", e.target.value)} className={inputCls} /></Field>}
          </div>
          {(f.recurrence.freq === "WEEKLY" || f.recurrence.freq === "CUSTOM") && (
            <div className="flex items-center gap-2">
              {f.recurrence.freq === "CUSTOM" && <><span className="text-xs text-muted-foreground">Toutes les</span><input type="number" min={1} value={f.recurrence.interval} onChange={(e) => setR("interval", Number(e.target.value) || 1)} className="w-14 rounded-lg border border-border bg-background px-2 py-1 text-sm" /><span className="text-xs text-muted-foreground">semaines, le</span></>}
              <div className="flex gap-1">{WDAYS.map(([v, l]) => (
                <button type="button" key={v} data-testid={`event-byday-${v}`} onClick={() => setR("byweekday", f.recurrence.byweekday.includes(v) ? f.recurrence.byweekday.filter((x) => x !== v) : [...f.recurrence.byweekday, v])}
                  className={`w-7 h-7 rounded-full text-xs ${f.recurrence.byweekday.includes(v) ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
              ))}</div>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Source · AIRTIME — Manuel</span>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-accent">Annuler</button>
              <button type="submit" data-testid="event-submit" disabled={saving || !f.title.trim()} className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground font-medium disabled:opacity-50">{submitLabel}</button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
